import { Controller } from "@hotwired/stimulus"

// Set aside (budgets/_renewals_setaside), design A: the gauge jar and its
// shelf. Right after a set-aside the page renders the fills from before it;
// once the card is on screen this pours the money in, by Disney's 12:
//
//   staging        the sheet has closed; one jar at a time takes the big
//                  jar's place, the shelf waits still under it
//   anticipation   the jar braces (squats) before the chip lands; the jar
//                  and copy pull back 6px before they hand over
//   squash/stretch the chip stretches in flight and squashes on landing;
//                  the jar squashes as the pour hits it
//   straight ahead the waves run on their own; the catch, the pour and the
//   / pose to pose lid are keyed poses
//   follow-through liquid and meter run past their mark and settle; the jar
//                  wobbles three times, smaller each time
//   overlap        number, meter and % move together; the copy trails the
//                  jar by 60ms on a hand-over
//   slow in/out    every number counts on an ease in-out curve
//   arcs           the chip flies up from the sheet's button on a curve
//   secondary      the shelf's check box pulses with the big jar
//   timing         560ms flight, 700ms counts, 1000ms fills, a 700ms hold
//                  on a jar that just became Ready
//   exaggeration   kept for Ready only: lid drops on, tick pops, the chip
//                  flips to green
//   solid drawing  the chip turns in 3D; the glass keeps its highlight
//   appeal         a part amount stays calm: no lid, no tick
//
// One jar or twelve: the pour walks every jar the money reached, in order;
// when the last one fills the card turns All set (everything green). Every
// figure ends on the server's own text. Reduced Motion: a 200ms fade to the
// final state, no flight, no pour.
const EASE_OUT = "cubic-bezier(0.23, 1, 0.32, 1)"
const SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)"
const HOLD_READY_MS = 700

export default class extends Controller {
  static targets = ["card", "cta", "bigJar", "bigLiq", "copy", "chip", "name", "have", "of", "meter", "meta", "plan", "readyCount", "mini"]
  static values = { landed: Number, spot: Number, symbol: String, symbolFirst: Boolean, jars: Array, texts: Object }

  connect() {
    this.spotIndex = this.jarsValue.findIndex((jar) => jar.state_before !== "ready")
    if (this.spotIndex < 0) this.spotIndex = 0
    if (this.landedValue <= 0) return

    // The landing plays once: a cached snapshot keeps the settled jars and
    // no landing, so coming back to the dashboard does not pour it again.
    this.beforeCache = () => {
      this.cancelled = true
      this.chipEl?.remove()
      this.paintFinal()
      this.landedValue = 0
    }
    document.addEventListener("turbo:before-cache", this.beforeCache, { once: true })

    this.reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return
      this.observer.disconnect()
      this.reduce ? this.settle() : this.land()
    }, { threshold: 0.5 })
    this.observer.observe(this.element)
  }

  disconnect() {
    if (this.beforeCache) document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.observer?.disconnect()
    this.chipEl?.remove()
    this.cancelled = true
  }

  settle() {
    this.element.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 200 })
    this.paintFinal()
  }

  async land() {
    await this.fly()
    const poured = this.jarsValue.map((jar, i) => ({ jar, i })).filter(({ jar }) => jar.after > jar.before)
    for (const { i } of poured) {
      if (this.cancelled) return
      await this.pour(i)
    }
    if (this.cancelled) return
    if (this.spotValue !== this.spotIndex) await this.handOver(this.spotValue, "after")
    this.paintFinal()
  }

  // Arc + squash/stretch: from where the sheet's button was into the jar.
  async fly() {
    const chip = document.createElement("div")
    chip.className = "rs-drop font-mono"
    chip.textContent = this.textsValue.chip
    document.body.append(chip)
    this.chipEl = chip

    const target = this.bigJarTarget.getBoundingClientRect()
    const w = chip.offsetWidth
    const h = chip.offsetHeight
    const sx = window.innerWidth / 2 - w / 2
    const sy = window.innerHeight - 120
    const ex = target.left + target.width / 2 - w / 2
    const ey = target.top + target.height * 0.35 - h / 2
    const mx = (sx + ex) / 2 + 70
    const my = Math.min(sy, ey) - 90
    const at = (t) => [
      (1 - t) ** 2 * sx + 2 * (1 - t) * t * mx + t * t * ex,
      (1 - t) ** 2 * sy + 2 * (1 - t) * t * my + t * t * ey
    ]
    const frames = [0, 0.2, 0.45, 0.7, 0.9, 1].map((t) => {
      const [x, y] = at(t)
      const shape = t === 1 ? "scale(1.25, 0.7)" : (t > 0.1 && t < 0.9 ? "scale(0.92, 1.12)" : "scale(1)")
      return { transform: `translate(${x}px, ${y}px) ${shape}`, opacity: t === 1 ? 0.2 : 1, offset: t }
    })

    this.bigJarTarget.animate(
      [{ transform: "scale(1)" }, { transform: "scale(1.04, 0.94)", offset: 0.8 }, { transform: "scale(0.98, 1.03)", offset: 0.92 }, { transform: "scale(1)" }],
      { duration: 620, easing: EASE_OUT }
    )
    await chip.animate(frames, { duration: 560, easing: "cubic-bezier(.45,.05,.55,.95)", fill: "forwards" }).finished
    chip.remove()
    this.chipEl = null
  }

  // One jar takes the pour: into the big jar's place if it is not there yet,
  // then it fills, squashes and wobbles; Ready gets its lid, tick and green chip.
  async pour(i) {
    const jar = this.jarsValue[i]
    if (i !== this.spotIndex) await this.handOver(i, "before")

    this.bigJarTarget.animate(
      [{ transform: "none" }, { transform: "scale(1.07, 0.9)", offset: 0.18 }, { transform: "rotate(-4deg)", offset: 0.42 },
        { transform: "rotate(2.5deg)", offset: 0.62 }, { transform: "rotate(-1deg)", offset: 0.8 }, { transform: "none" }],
      { duration: 780, easing: "ease-out" }
    )
    const pct = this.fillPct(jar, "after")
    this.bigLiqTarget.style.height = `${pct}%`
    this.meterTarget.style.width = `${pct}%`
    this.pulseMini(i)

    await Promise.all([
      this.tween(jar.before, jar.after, 700, (v) => { this.haveTarget.textContent = this.money(Math.round(v)) }),
      this.tween(this.pctOf(jar.before, jar.amount), jar.pct, 700, (v) => { this.paintMeta(jar, Math.round(v)) })
    ])
    this.haveTarget.textContent = jar.have_text

    if (jar.state === "ready") await this.becomeReady(i)
    else this.setState(jar.state)
  }

  // Exaggeration, only for the moment that earns it.
  async becomeReady(i) {
    await this.wait(150)
    this.setJarState(this.bigJarTarget, "ready")
    this.bigJarTarget.querySelector(".rs-lid").animate(
      [{ transform: "translateY(-20px) rotate(-16deg)", opacity: 0 }, { transform: "translateY(2px) scaleY(0.7)", opacity: 1, offset: 0.65 }, { transform: "none", opacity: 1 }],
      { duration: 560, easing: SPRING }
    )
    this.bigJarTarget.querySelector(".rs-tick").animate(
      [{ transform: "scale(0.3)", opacity: 0 }, { transform: "scale(1.25)", opacity: 1, offset: 0.6 }, { transform: "none", opacity: 1 }],
      { duration: 480, delay: 180, easing: EASE_OUT }
    )
    this.flipChip("ready")
    this.setMiniState(i, "ready")
    this.bumpReady()
    if (this.hasPlanTarget) this.swap(this.planTarget, () => { this.planTarget.replaceChildren(...this.fill(this.textsValue.covered, { month: this.bold(this.jarsValue[i].month) })) })
    await this.wait(HOLD_READY_MS)
  }

  // Staging: the hand-over. Anticipation (a 6px pull back), exit, then the
  // next jar enters past its mark and settles; the copy follows 60ms behind.
  async handOver(i, phase) {
    const parts = [this.bigJarTarget, this.copyTarget]
    await Promise.all(parts.map((el, k) => el.animate(
      [{ transform: "none", opacity: 1 }, { transform: "translateX(6px)", opacity: 1, offset: 0.25 }, { transform: "translateX(-24px)", opacity: 0 }],
      { duration: 220, delay: k * 40, easing: "ease-in", fill: "forwards" }
    ).finished))
    this.paintSpot(i, phase)
    parts.forEach((el, k) => el.animate(
      [{ transform: "translateX(24px)", opacity: 0 }, { transform: "translateX(-3px)", opacity: 1, offset: 0.7 }, { transform: "none", opacity: 1 }],
      { duration: 460, delay: k * 60, easing: EASE_OUT, fill: "forwards" }
    ))
    await this.wait(380)
  }

  paintSpot(i, phase) {
    const jar = this.jarsValue[i]
    const state = phase === "after" ? jar.state : jar.state_before
    const fill = phase === "after" ? jar.after : jar.before
    const pct = this.fillPct(jar, phase)
    this.spotIndex = i
    this.setState(state)
    const bars = [this.bigLiqTarget, this.meterTarget]
    bars.forEach((el) => { el.style.transition = "none" })
    this.bigLiqTarget.style.height = `${pct}%`
    this.meterTarget.style.width = `${pct}%`
    this.bigLiqTarget.getBoundingClientRect() // commit the start before the pour's transition
    bars.forEach((el) => { el.style.transition = "" })
    this.nameTarget.textContent = jar.name
    this.haveTarget.textContent = phase === "after" ? jar.have_text : this.money(Math.round(fill))
    const amount = document.createElement("bdi")
    amount.dir = "ltr"
    amount.textContent = jar.amount_text
    this.ofTarget.replaceChildren(...this.fill(this.textsValue.of, { amount }))
    this.paintMeta(jar, this.pctOf(fill, jar.amount))
    this.miniTargets.forEach((mini, k) => mini.classList.toggle("is-spot", k === i))
  }

  paintMeta(jar, pct) {
    this.metaTarget.textContent = this.fill(this.textsValue.meta, { pct, date: jar.date_text, in_days: jar.in_days }).join("")
  }

  // Every figure on the server's text: the end of a landing, or Reduced Motion.
  paintFinal() {
    this.jarsValue.forEach((jar, i) => this.setMiniState(i, jar.state))
    this.paintSpot(this.spotValue, "after")
    if (this.hasReadyCountTarget) this.readyCountTarget.textContent = this.jarsValue.filter((jar) => jar.state === "ready").length
    if (this.jarsValue.every((jar) => jar.state === "ready")) this.allSet()
  }

  // All set: the same card, everything green.
  allSet() {
    this.cardTarget.classList.add("is-done")
    this.chipTarget.className = "rs-chip is-ready"
    this.chipTarget.textContent = this.textsValue.chips.all
    this.miniTargets.forEach((mini) => mini.classList.remove("is-spot"))
    if (this.hasCtaTarget) this.ctaTarget.remove()
  }

  setState(state) {
    this.setJarState(this.bigJarTarget, state)
    this.setChip(state)
  }

  setJarState(el, state) {
    el.classList.remove("is-ready", "is-filling", "is-waiting")
    el.classList.add(`is-${state}`)
  }

  // Solid drawing: the chip turns over on its X axis, the new state on its back.
  flipChip(state) {
    this.chipTarget.animate([{ transform: "rotateX(0)" }, { transform: "rotateX(90deg)" }], { duration: 160, easing: "ease-in" }).finished.then(() => {
      this.setChip(state)
      this.chipTarget.animate([{ transform: "rotateX(-90deg)" }, { transform: "rotateX(0)" }], { duration: 300, easing: SPRING })
    })
  }

  setChip(state) {
    this.chipTarget.className = `rs-chip is-${state}`
    this.chipTarget.textContent = this.textsValue.chips[state]
  }

  // Secondary action: the shelf jar fills and pulses with the big one.
  pulseMini(i) {
    const mini = this.miniTargets[i]
    if (!mini) return
    mini.animate([{ transform: "scale(1)" }, { transform: "scale(1.35)", offset: 0.4 }, { transform: "scale(1)" }], { duration: 520, easing: SPRING })
  }

  setMiniState(i, state) {
    const mini = this.miniTargets[i]
    if (mini) this.setJarState(mini, state)
  }

  bumpReady() {
    if (!this.hasReadyCountTarget) return
    this.readyCountTarget.textContent = parseInt(this.readyCountTarget.textContent, 10) + 1
    this.readyCountTarget.animate([{ transform: "scale(1)" }, { transform: "scale(1.3)", offset: 0.4 }, { transform: "scale(1)" }], { duration: 360, easing: EASE_OUT })
  }

  swap(el, paint) {
    el.animate([{ opacity: 1 }, { opacity: 0, transform: "translateY(-4px)" }], { duration: 140, fill: "forwards" }).finished.then(() => {
      paint()
      el.animate([{ opacity: 0, transform: "translateY(4px)" }, { opacity: 1, transform: "none" }], { duration: 220, easing: EASE_OUT, fill: "forwards" })
    })
  }

  // A translated template ("of %{amount}") as nodes: text runs and the
  // values in their places, so an element (a <bdi>, a <b>) keeps its markup.
  fill(template, values) {
    return template.split(/(%\{\w+\})/).filter(Boolean).map((part) => {
      const key = part.match(/^%\{(\w+)\}$/)?.[1]
      if (!key || !(key in values)) return part
      const value = values[key]
      return value instanceof Node ? value : String(value)
    })
  }

  bold(text) {
    const b = document.createElement("b")
    b.textContent = text
    return b
  }

  fillPct(jar, phase) {
    const fill = phase === "after" ? jar.after : jar.before
    return jar.amount > 0 ? Math.min(fill / jar.amount * 100, 100) : 100
  }

  pctOf(fill, amount) {
    return amount > 0 ? Math.floor(fill / amount * 100) : 100
  }

  // Slow in, slow out.
  tween(from, to, ms, paint) {
    return new Promise((resolve) => {
      const start = performance.now()
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)
      const tick = (now) => {
        const p = Math.min((now - start) / ms, 1)
        paint(from + (to - from) * ease(p))
        p < 1 ? requestAnimationFrame(tick) : resolve()
      }
      requestAnimationFrame(tick)
    })
  }

  money(amount) {
    const number = amount.toLocaleString(document.documentElement.lang || undefined)
    return this.symbolFirstValue ? `${this.symbolValue}${number}` : `${number}${this.symbolValue}`
  }

  wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}
